<template>
  <div class="date-picker-wrapper">
    <div ref="datePicker" />
    <div class="widget-quickDateSet--opts" v-if="showDefaultShortcuts">
      <span :key="index" v-for="(shortcut, index) in defaultShortcuts">
        <span v-if="index">|</span>
        <button
          @click="
            setCustomDate(shortcut.span, shortcut.um, shortcut.relativeToToday)
          "
        >
          {{ shortcut.label }}
        </button>
      </span>
    </div>
    <div class="widget-quickDateSet--opts" v-else-if="shortcuts.length">
      <span :key="index" v-for="(shortcut, index) in shortcuts">
        <span v-if="index">|</span>
        <button
          @click="
            setCustomDate(shortcut.span, shortcut.um, shortcut.relativeToToday)
          "
        >
          {{ shortcut.label }}
        </button>
      </span>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import debounce from 'lodash/debounce';
import rome from '@teamwork/rome/dist/rome.min';

export default {
  props: {
    value: {
      default: null,
      validator: (prop) => prop === null || moment.isMoment(prop),
    },
    /*
      Example of shortcuts
      label: string;
      span: number; // number of days to jump to from the current
                      value, in case of undefined it will treat it as no date
      um: string; // defaults to `days` more: https://momentjs.com/docs/#/manipulating/add/
      relativeToToday: boolean; // Adds span to todays date (ie: Next monday)
      [{label: 'Today', span: 0 }, {label: 'None'}]
    */
    shortcuts: { type: Array, required: false, default: () => [] },
    showDefaultShortcuts: { type: Boolean, default: false },
  },
  mounted() {
    this.createPicker();
  },
  methods: {
    setDate: debounce(function setDate(date, options, send) {
      if (send) {
        const mo = moment(date, options.inputFormat);
        this.$emit('input', mo.isValid() ? mo : null);
      }
    }, 200),
    createPicker() {
      const defaultOptions = {
        weekStart: window.app.loggedInUser.localization.startOnSunday() ? 0 : 1,
        weekdayFormat: [
          Vue.t('Sunday').charAt(0),
          Vue.t('Monday').charAt(0),
          Vue.t('Tuesday').charAt(0),
          Vue.t('Wednesday').charAt(0),
          Vue.t('Thursday').charAt(0),
          Vue.t('Friday').charAt(0),
          Vue.t('Saturday').charAt(0),
        ],
        monthFormat: 'MMM YYYY',
      };

      const options = {
        time: false,
        dayFormat: 'D',
        weekdayFormat: 'short',
        inputFormat: 'YYYYMMDD',
        initialValue: this.value && this.value.clone(),
        autoHideOnClick: true,
        monthsInCalendar: 1,
        autoClose: true,
        min: this.min,
        max: this.max,
        ...defaultOptions,
        ...this.pickerOptions,
      };

      if (this.picker) {
        this.picker.destroy();
        this.picker = null;
      }

      const element = this.$refs.datePicker;
      if (element) {
        delete element.dataset.romeId;
        this.picker = rome(element, options);

        this.picker.on('data', (val) => {
          if (val) {
            this.setDate(val, options, true);
          }
        });

        this.picker.on('back', (val) => {
          this.setDate(val, options, false);
        });
        this.picker.on('next', (val) => {
          this.setDate(val, options, false);
        });
      }
    },
    setCustomDate(span, um = 'days', relativeToToday = false) {
      if (typeof span === 'undefined') {
        this.$emit('input', null);
        return;
      }

      if (this.picker) {
        let current = moment(this.picker.getDate());
        if (span) {
          if (relativeToToday) {
            // If this is relativeToToday, then set it to today+span (relative from today)
            current = moment().add(span, um);
          } else {
            current = current.clone().add(span, um);
          }
        } else {
          current = moment();
        }

        this.picker.setValue(current);
        this.$emit('input', current);
      }
    },
  },
  computed: {
    pickerOptions: ({ $attrs: { value, ...rest } }) => rest,
    min: (vm) => vm.pickerOptions.min,
    max: (vm) => vm.pickerOptions.max,
    defaultShortcuts() {
      const shortcuts = [
        { label: Vue.t('Today'), span: 0 },
        { label: Vue.t('+[0] Day', 1), span: 1 },
        { label: Vue.t('+[0] Days', 7), span: 7 },
      ];

      if ([5, 6, 0].includes(moment().day())) {
        const daysToMon = 8 - moment().day();
        shortcuts.push({
          label: Vue.t('Mon'),
          span: daysToMon,
          relativeToToday: true,
        });
      }

      shortcuts.push({ label: Vue.t('None') });

      return shortcuts;
    },
  },
  watch: {
    value() {
      if (this.picker) {
        this.createPicker();
      }
    },
    min() {
      if (this.picker) {
        this.createPicker();
      }
    },
    max() {
      if (this.picker) {
        this.createPicker();
      }
    },
  },
};
</script>

<style lang="scss">
// don't use 'scoped' here, style won't get override for datepicker otherwise
// there are many css files in TKO overriding datepicker styles
// deep selector is not working even as those legacy styles are taking precedence
@import '~@tko/src/styles/variables/variables';

$font-size--header: 14px;
$margin--btn-back: 10px 0px 0px 10px;
$margin--btn-next: 10px 10px 0px 0px;
$padding--label: 11px 0px;
$padding--days: 12px 12px 4px 12px;
$padding--quick-set: 14px 0px 20px;
$width--day: 36px;
$height--day: 36px;
$max-width--quick-set: 252px;

@mixin circleBg($background-color: $neutral-50) {
  content: '';
  background-color: $background-color;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: block;
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: -1;
}

@mixin chevron($rotate: 0deg) {
  background: transparent;
  transform: rotate($rotate) scale(1.5);

  &::before {
    border-color: transparent;
    content: '\F107';
    top: 25%;
    font-size: 14px;
    color: $color--text-tinted-dark;
    font-family: 'FontAwesome';
  }
}

.date-picker-wrapper {
  height: 100%;
  justify-content: space-between;
  display: flex;
  flex-direction: column;
  min-height: 352px;

  .widget-quickDateSet--opts {
    padding: 10px 0;
    border-top: 1px solid $neutral-50;
    padding: $padding--quick-set;
    max-width: $max-width--quick-set;
    margin: 0 auto;

    span {
      span {
        margin: 0 $margin--small;
        color: $neutral-50;
      }
    }

    button {
      border: none;
      background-color: transparent;
      font-size: $font-size--small;
      padding: 0;
      color: #4461d7;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .rd-container {
    height: auto;
    border-bottom: none;
    background-color: transparent;
  }

  .rd-month-label {
    color: $color--text-tinted-dark;
    background-color: transparent;
    font-size: $font-size--header;
    font-weight: $font-weight--semibold;
    padding: $padding--label;
  }

  .rd-back {
    margin: $margin--btn-back;
    @include chevron(90deg);
  }

  .rd-next {
    margin: $margin--btn-next;
    @include chevron(-90deg);
  }

  .rd-days {
    margin: 0;
    padding: $padding--days;
  }

  .rd-day-head {
    color: $color--text-tinted-dark;
    font-size: $font-size--header;
    font-weight: $font-weight--semibold;
    text-align: center;
    padding-bottom: $margin--small;
  }

  .rd-day-body {
    position: relative;
    text-align: center;
    cursor: pointer;
    z-index: 0;
    width: $width--day;
    height: $height--day;
    color: $color--text-tinted-dark;

    &.rd-day-today::before {
      @include circleBg();
    }

    &:not(.rd-day-selected):hover::before {
      @include circleBg(#f5f7fa);
    }

    &.rd-day-selected {
      color: #fff;

      &::before {
        @include circleBg($background-color--new-primary);
      }
    }

    &.rd-day-prev-month,
    &.rd-day-next-month,
    &.rd-day-disabled {
      color: #c5cee0;
    }

    &.rd-day-disabled {
      pointer-events: none;
    }
  }
}
</style>
